import React, { createContext, useState, useContext } from 'react';
import { questionSections as questions } from '../data/questions';
import { supabase } from '../config/supabase';
import { generateGameplan } from '../utils/gameplanGenerator';

const QuestionnaireContext = createContext();

export const useQuestionnaire = () => useContext(QuestionnaireContext);

export const QuestionnaireProvider = ({ children }) => {
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [clientInfo, setClientInfo] = useState(null);
  const [gameplan, setGameplan] = useState(null);

  const handleAnswer = (questionId, answer) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: answer,
    }));
  };

  // Helper to update answers (alias for handleAnswer to match usage in QuestionnairePage if needed, 
  // though QuestionnairePage uses updateResponse from useQuestionnaire which is not exported here. 
  // Wait, QuestionnairePage uses `responses` and `updateResponse`.
  // The context provider below exports `answers` and `handleAnswer`.
  // I should check if I need to align the exported names with what QuestionnairePage expects.
  // QuestionnairePage has: const { responses, updateResponse, completeQuestionnaire } = useQuestionnaire();
  // But this file exports: value = { answers, ..., handleAnswer, ... }
  // This is a discrepancy. I should fix the exported names to match usage in QuestionnairePage.
  
  const updateResponse = handleAnswer;
  const responses = answers;

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const startQuestionnaire = (info) => {
    setClientInfo(info);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setGameplan(null);
  };

  const saveGameplan = async (gameplanToSave) => {
    const { data, error } = await supabase
      .from('gameplans')
      .upsert(gameplanToSave, { onConflict: 'client_id' });

    if (error) {
      console.error('Error saving gameplan:', error);
      throw error;
    }

    return data;
  };

  const completeQuestionnaire = async () => {
    // Ensure clientInfo is available. If not, we might be completing from a state where we just have answers.
    // However, clientInfo is required for the gameplan.
    if (!clientInfo) {
        console.error("Missing client info");
        throw new Error("Missing client info");
    }

    const generatedPlan = generateGameplan(clientInfo, answers);
    const gameplanToSave = {
      client_id: clientInfo.email,
      client_name: clientInfo.name,
      client_info: clientInfo,
      answers: answers,
      gameplan: generatedPlan,
    };

    try {
      await saveGameplan(gameplanToSave);
      setGameplan(generatedPlan);
      return generatedPlan;
    } catch (error) {
      console.error("Failed to complete and save questionnaire:", error);
      throw error;
    }
  };
  
  const resetQuestionnaire = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setClientInfo(null);
    setGameplan(null);
  };

  const value = {
    answers,
    responses, // Export as responses to match QuestionnairePage
    currentQuestionIndex,
    questions,
    handleAnswer,
    updateResponse, // Export as updateResponse to match QuestionnairePage
    nextQuestion,
    prevQuestion,
    startQuestionnaire,
    completeQuestionnaire,
    clientInfo,
    gameplan,
    resetQuestionnaire
  };

  return (
    <QuestionnaireContext.Provider value={value}>
      {children}
    </QuestionnaireContext.Provider>
  );
};